import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import PropTypes from 'prop-types';
import { p as props } from './componentEl-fcbc301e.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';
import { a as addSpacingClasses, p as paddingPropTypes, m as marginPropTypes } from './spacing-0f39ba4a.js';

function TableCell({
  align,
  componentEl,
  children,
  className,
  padding,
  margin,
  isLabel,
  valign,
  ...otherProps
}) {
  let cssClasses = ['st_tablecell', `st_tablecell--align-${align}`, `st_tablecell--valign-${valign}`];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    padding,
    margin,
    cssClasses
  });

  // this is set here and not as defaultProps
  // because Table.Row automatically makes the element th
  // if the row is a header row
  // and we cant detect that a user didn't set a componentEl
  // if we have defaultProps in place
  const Component = componentEl || 'td';
  if (isLabel) {
    cssClasses.push('st_tablecell--header');
  } else {
    cssClasses.push('st_tablecell--normal');
  }
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
TableCell.propTypes = {
  /**
   * dom element or component to use, defaults to TD if it's a regular cell,
   * and TH if it's a header cell
   * */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** padding for cell. Setting this overrides defaults and does not extend them. */
  padding: paddingPropTypes,
  /** margin for cell, if any */
  margin: marginPropTypes,
  /** scope for table header, if any */
  scope: PropTypes.oneOf(['row', 'col', 'rowgroup', 'colgroup']),
  /** whether this appears as a label, or regular text */
  isLabel: PropTypes.bool,
  /** how to align cell contents */
  align: PropTypes.oneOf(['left', 'right', 'center']),
  /** how to vertically align cell contents */
  valign: PropTypes.oneOf(['baseline', 'top', 'bottom', 'middle'])
};
TableCell.defaultProps = {
  align: 'left',
  componentEl: null,
  className: null,
  margin: null,
  padding: {
    vertical: 'sm',
    horizontal: 'md'
  },
  scope: null,
  isLabel: false,
  valign: 'baseline'
};
TableCell.displayName = 'Table.Cell';

function TableRow({
  componentEl: Component,
  children,
  className,
  padding,
  margin,
  isHeader,
  hoverable,
  ...otherProps
}) {
  let cssClasses = ['st_table__row'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    padding,
    margin,
    cssClasses
  });
  if (hoverable) cssClasses.push('st_table__row--hoverable');
  let childrenWithProps = children;

  // if this is a header row, make all cells inside it header cells
  // and add scope="col"
  if (isHeader) {
    cssClasses.push('st_table__row--header');
    childrenWithProps = React.Children.map(children, child => {
      if (! /*#__PURE__*/React.isValidElement(child) || child.type.displayName !== 'Table.Cell') {
        return child;
      }

      // if it's an empty cell that has no element defined
      // make it a TD and don't add scope/th
      if (!child.props.componentEl && !child.props.children) {
        return /*#__PURE__*/React.cloneElement(child, {
          componentEl: 'td',
          isLabel: true
        });
      }

      // if cell has content, default to <th>, mark as header, and add scope if not set
      return /*#__PURE__*/React.cloneElement(child, {
        componentEl: child.props.componentEl || 'th',
        isLabel: true,
        scope: typeof child.props.scope !== 'undefined' && child.props.scope || 'col'
      });
    });
  }
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' '),
    role: "row"
  }, otherProps), childrenWithProps);
}
TableRow.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** padding for cell, if any */
  padding: paddingPropTypes,
  /** margin for cell, if any */
  margin: marginPropTypes,
  /** whether it's a header row or regular row */
  isHeader: PropTypes.bool,
  /** change background color on hover  */
  hoverable: PropTypes.bool
};
TableRow.defaultProps = {
  componentEl: 'tr',
  className: null,
  margin: null,
  padding: null,
  isHeader: false,
  hoverable: false
};
TableRow.displayName = 'Table.Row';

function TableBody({
  componentEl: Component,
  children,
  className,
  padding,
  margin,
  ...otherProps
}) {
  let cssClasses = ['st_table__body'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    padding,
    margin,
    cssClasses
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
TableBody.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** padding for list item, if any */
  padding: paddingPropTypes,
  /** margin for list item, if any */
  margin: marginPropTypes
};
TableBody.defaultProps = {
  componentEl: 'tbody',
  className: null,
  margin: null,
  padding: null
};
TableBody.displayName = 'Table.Body';

function TableHead({
  componentEl: Component,
  children,
  className,
  padding,
  margin,
  ...otherProps
}) {
  let cssClasses = ['st_table__head'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    padding,
    margin,
    cssClasses
  });
  const childrenWithProps = React.Children.map(children, child => {
    if (! /*#__PURE__*/React.isValidElement(child) || child.type.displayName !== 'Table.Row') {
      return child;
    }
    return /*#__PURE__*/React.cloneElement(child, {
      isHeader: true
    });
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), childrenWithProps);
}
TableHead.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** padding for cell, if any */
  padding: paddingPropTypes,
  /** margin for cell, if any */
  margin: marginPropTypes
};
TableHead.defaultProps = {
  componentEl: 'thead',
  className: null,
  margin: null,
  padding: null
};
TableHead.displayName = 'Table.Head';

const bordersDictionary = {
  horizontal: 'hor',
  all: 'all'
};
function Table({
  border,
  componentEl: Component,
  children,
  className,
  margin,
  fullWidth,
  ...otherProps
}) {
  let cssClasses = ['st_table'];
  cssClasses = addClassNames(className, cssClasses);
  if (fullWidth) cssClasses.push('st_width--full');
  if (border !== 'all') cssClasses.push(`st_table--border-${bordersDictionary[border]}`);
  let wrapperClasses = ['st_tablewrapper'];
  wrapperClasses = addSpacingClasses({
    margin,
    cssClasses: wrapperClasses
  });
  return /*#__PURE__*/React.createElement("div", {
    className: wrapperClasses.join(' ')
  }, /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' '),
    role: "table"
  }, otherProps), children));
}
Table.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for table, if any */
  margin: marginPropTypes,
  /** whether table should take up 100% of available space, or shrink wrap to fit content  */
  fullWidth: PropTypes.bool,
  /**  which borders to show between cells */
  border: PropTypes.oneOf(['all', 'horizontal'])
};
Table.defaultProps = {
  componentEl: 'table',
  className: null,
  margin: {
    all: 'none'
  },
  fullWidth: false,
  border: 'all'
};
Table.Cell = TableCell;
Table.Head = TableHead;
Table.Body = TableBody;
Table.Row = TableRow;

export { Table as T };
